// src/pages/admin/organization-chart.tsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./organization-chart.css";
import { all_routes } from "../../../feature-module/router/all_routes";
import Loader from "../loader";
import CollapseHeader from "../collapse-header/collapse-header";
import api from "../../../services/api";
import { useSelector } from "react-redux";
import CommonSelect from "../commonSelect";
import { showBasicAlert } from "../useAlert";

// Type definitions
interface Company {
  id: number;
  name: string;
  type: string;
  [key: string]: any;
}

interface Employee {
  key: string | number;
  id: string | number;
  Name: string;
  Designation?: string;
  Department?: string;
  Email: string;
  Phone: string;
  Image?: string;
  Status: string;
  CompanyId: number;
  CompanyType: string;
  CompanyName: string;
  ReportingOfficerId?: number | null;
  reporting_officer?: number | null;
  designation?: number;
  department?: number;
  role_char?: string;
  responsibilities?: string[];
  [key: string]: any;
}

interface Department {
  key: string | number;
  Department: string;
  [key: string]: any;
}

interface Designation {
  key: string | number;
  Designation: string;
  name?: string;
  [key: string]: any;
}

interface OrgNode {
  id: string;
  type: "company" | "department" | "designation" | "employee" | "role";
  name: string;
  title: string;
  department?: string;
  email?: string;
  phone?: string;
  photo?: string;
  level?: number;
  children: OrgNode[];
  expanded?: boolean;
  employeeData?: Employee;
  isCurrentUser?: boolean;
}

const OrganizationChart: React.FC = () => {
  const signature = localStorage.getItem("signature") || "";
  const user = useSelector((state: any) => {
    return state.user.user;
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [organizationData, setOrganizationData] = useState<OrgNode[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [zoomLevel, setZoomLevel] = useState<number>(1);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany && user) {
      fetchOrganizationData();
    }
  }, [selectedCompany, user]);

  useEffect(() => {
    if (organizationData.length > 0 && user) {
      const expandCurrentUserChain = () => {
        const findNode = (nodes: OrgNode[]): OrgNode | null => {
          for (const node of nodes) {
            if (node.isCurrentUser) {
              return node;
            }
            if (node.children.length > 0) {
              const found = findNode(node.children);
              if (found) return found;
            }
          }
          return null;
        };

        const currentNode = findNode(organizationData);
        if (currentNode) {
          const newExpanded = new Set(expandedNodes);
          newExpanded.add(currentNode.id);
          setExpandedNodes(newExpanded);
        }
      };

      setTimeout(expandCurrentUserChain, 500);
    }
  }, [organizationData, user]);

  const fetchCompanies = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await api.post("userService/getAllCompanyList", {
        signature,
      });
      const companies: Company[] = response.data.data.data || [];
      setAllCompanies(companies);
      if (companies.length > 0) {
        setSelectedCompany(companies[0]);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrganizationData = async (): Promise<void> => {
    if (!selectedCompany || !user) {
      return;
    }

    try {
      setIsLoading(true);
      const [employeesResponse, departmentsResponse, designationsResponse] =
        await Promise.all([
          api.post("userService/getAllEmployeesListData", { signature }),
          api.post("userService/getDepartment", { signature }),
          api.post("userService/getDesignation", { signature }),
        ]);

      const allEmployees: Employee[] =
        employeesResponse.data.data.data.allUsers || [];
      const departments: Department[] =
        departmentsResponse.data.data.data || [];
      const designations: Designation[] =
        designationsResponse.data.data.data || [];

      const companyEmployees: Employee[] = allEmployees.filter(
        (emp) =>
          emp.CompanyId === selectedCompany.id &&
          emp.CompanyType === selectedCompany.type &&
          emp.Status === "Active"
      );

      const structure: OrgNode[] = buildProperHierarchy(
        companyEmployees,
        departments,
        designations,
        selectedCompany,
        user
      );

      setOrganizationData(structure);
    } catch (error) {
      console.error("Error fetching organization data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get company options for CommonSelect
  const getCompanyOptions = () => {
    if (!allCompanies || allCompanies.length === 0) return [];

    return allCompanies.map((company: Company) => ({
      value: company.id + "-" + company.type,
      label: company.name + " - " + company.type,
    }));
  };

  // Updated company change handler for CommonSelect
  const handleCompanyChange = (value: any) => {
    if (!value) {
      setSelectedCompany(null);
      setExpandedNodes(new Set());
      setOrganizationData([]);
      return;
    }

    const [companyId, companyType] = value.value.split("-");
    const company = allCompanies.find(
      (c) => c.id === parseInt(companyId) && c.type === companyType
    );

    if (company) {
      setSelectedCompany(company);
      setExpandedNodes(new Set());
    }
  };

  const buildProperHierarchy = (
    employees: Employee[],
    departments: Department[],
    designations: Designation[],
    company: Company,
    currentUser: any
  ): OrgNode[] => {
    const companyNode: OrgNode = {
      id: `company-${company.id}`,
      type: "company",
      name: company.name,
      title: "Company",
      children: [],
      expanded: true,
    };

    // Create a map of employees by their ID
    const employeeMap = new Map<string | number, Employee>();
    const employeeToNodeMap = new Map<string | number, OrgNode>();

    let currentUserCount = 0;
    let currentUserName = "";

    employees.forEach((emp) => {
      const empId = emp.key || emp.id;
      employeeMap.set(empId, emp);

      const designation = designations.find(
        (des) =>
          des.key === emp.designation || des.Designation === emp.Designation
      );
      const department = departments.find(
        (dept) =>
          dept.key === emp.department || dept.Department === emp.Department
      );

      let isCurrentUser = false;

      if (currentUser?.userDetails) {
        const userDetails = currentUser.userDetails;

        const userEmail = userDetails.email;
        const userEmpId = userDetails.emp_id;
        const userId = userDetails.id;
        const userName = `${userDetails.fname} ${userDetails.lname}`;

        isCurrentUser = (
          (userEmail && emp.Email === userEmail) ||
          (userEmpId && emp.emp_id === userEmpId) ||
          (userEmpId && emp.EmpId === userEmpId) ||
          (userId && String(emp.id) === String(userId)) ||
          (userId && emp.UID === userId) ||
          (userName && emp.Name === userName)
        );

        if (isCurrentUser) {
          currentUserCount++;
          currentUserName = emp.Name;
        }
      }

      const node: OrgNode = {
        id: `emp-${empId}`,
        type: "employee",
        name: emp.Name,
        title: designation?.Designation || emp.Designation || "Employee",
        department: department?.Department || emp.Department || "Unassigned",
        email: emp.Email,
        phone: emp.Phone,
        photo: emp.Image || "assets/img/users/user.png",
        children: [],
        employeeData: emp,
        isCurrentUser: isCurrentUser,
      };

      // Mark as CEO if no reporting officer
      if (!emp.ReportingOfficerId || emp.ReportingOfficerId === 0) {
        node.id = `ceo-${empId}`;
        node.title = "CEO / Chief Executive Officer";
      }

      employeeToNodeMap.set(empId, node);
    });

    const rootNodes: OrgNode[] = [];
    const childToParentMap = new Map<string | number, string | number>();

    employees.forEach((emp) => {
      const empId = emp.key || emp.id;
      const node = employeeToNodeMap.get(empId)!;

      if (emp.ReportingOfficerId && emp.ReportingOfficerId !== 0) {
        const parentNode = employeeToNodeMap.get(emp.ReportingOfficerId);
        if (parentNode) {
          parentNode.children.push(node);
          childToParentMap.set(empId, emp.ReportingOfficerId);
        } else {
          rootNodes.push(node);
        }
      } else {
        rootNodes.push(node);
      }
    });

    // Sort root nodes - put CEO first if exists
    rootNodes.sort((a, b) => {
      if (a.id.includes("ceo")) return -1;
      if (b.id.includes("ceo")) return 1;

      // Sort by department, then by name
      const deptA = a.department || "";
      const deptB = b.department || "";
      if (deptA !== deptB) return deptA.localeCompare(deptB);
      return a.name.localeCompare(b.name);
    });

    // Add root nodes to company
    companyNode.children = rootNodes;

    return [companyNode];
  };

  const toggleNode = (nodeId: string): void => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleFindCurrentUser = (): void => {
    if (!user) {
      console.error("User not available");
      return;
    }

    const findPathToUser = (nodes: OrgNode[]): string[] | null => {
      for (const node of nodes) {
        if (node.isCurrentUser) {
          return [node.id];
        }

        if (node.children.length > 0) {
          const childPath = findPathToUser(node.children);
          if (childPath) {
            return [node.id, ...childPath];
          }
        }
      }
      return null;
    };

    const userPath = findPathToUser(organizationData);

    if (!userPath) {
      showBasicAlert("Your profile is not found in the current organization chart.");
      return;
    }

    const newExpanded = new Set(expandedNodes);

    for (let i = 0; i < userPath.length - 1; i++) {
      newExpanded.add(userPath[i]);
    }

    // Update expanded nodes state
    setExpandedNodes(newExpanded);

    setTimeout(() => {
      const userNodeElement = document.querySelector('.org-node.current-user');

      if (userNodeElement) {
        userNodeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });

        userNodeElement.classList.add('highlight-find');
        setTimeout(() => {
          userNodeElement.classList.remove('highlight-find');
        }, 2000);
      } else {
        setTimeout(() => {
          const retryElement = document.querySelector('.org-node.current-user');
          if (retryElement) {
            retryElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center'
            });
            retryElement.classList.add('highlight-find');
            setTimeout(() => {
              retryElement.classList.remove('highlight-find');
            }, 2000);
          } else {
            console.error("Could not find current user element even after expanding parents");
          }
        }, 500);
      }
    }, 300); // Increased delay to ensure DOM updates
  };

  const renderTreeNode = (node: OrgNode, depth: number = 0): JSX.Element => {
    const isExpanded = expandedNodes.has(node.id) || node.expanded;

    return (
      <div
        key={node.id}
        className={`org-node org-node-${node.type} org-level-${depth} ${node.id.includes("ceo") ? "ceo-node" : ""
          } ${node.isCurrentUser ? "current-user" : ""}`}
        style={{ marginLeft: `${depth * 40}px` }}
      >
        <div
          className={`org-node-content ${node.children.length > 0 ? "has-children" : ""}`}
          onClick={() => node.children.length > 0 && toggleNode(node.id)}
        >
          <div className="node-type-indicator">
            {node.type === "company" && (
              <i className="ti ti-building-community"></i>
            )}
            {node.type === "department" && <i className="ti ti-building"></i>}
            {node.type === "designation" && <i className="ti ti-briefcase"></i>}
            {node.type === "employee" &&
              (node.isCurrentUser ? (
                <i className="ti ti-user-circle text-success"></i>
              ) : node.id.includes("ceo") ? (
                <i className="ti ti-crown"></i>
              ) : (
                <i className="ti ti-user"></i>
              ))}
          </div>

          <div className="node-details">
            {node.type === "employee" ? (
              <div className={`employee-card ${node.isCurrentUser ? "current-user" : ""}`}>
                <div className="employee-avatar">
                  <img
                    src={node.photo}
                    alt={node.name}
                    className="rounded-circle"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "assets/img/users/user.png";
                    }}
                  />
                </div>
                <div className="employee-info">
                  <h6 className="employee-name">
                    {node.name}
                    {node.isCurrentUser && (
                      <span className="badge bg-success ms-2">
                        <i className="ti ti-user-circle me-1"></i> You
                      </span>
                    )}
                    {node.id.includes("ceo") && (
                      <span className="ceo-badge">CEO</span>
                    )}
                  </h6>
                  <p className="employee-title">{node.title}</p>
                  {node.department && (
                    <span className="employee-department">
                      {node.department}
                    </span>
                  )}
                  <div className="employee-contact">
                    {node.email && (
                      <small className="text-muted">{node.email}</small>
                    )}
                    {node.phone && (
                      <small className="text-muted d-block">{node.phone}</small>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h5 className="node-name">{node.name}</h5>
                <p className="node-title">{node.title}</p>
                {/* {node.children && node.children.length > 0 && (
                  <span className="node-count">
                    {node.children.length} member
                    {node.children.length !== 1 ? "s" : ""}
                  </span>
                )} */}
              </>
            )}
          </div>

          {node.children.length > 0 && (
            <div className="expand-indicator">
              <i
                className={`ti ti-chevron-${isExpanded ? "down" : "right"}`}
              ></i>
            </div>
          )}
        </div>

        {isExpanded && node.children.length > 0 && (
          <div className="org-children">
            {node.children.map((child) => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderHierarchicalView = (): JSX.Element => {
    return (
      <div className="org-chart-container">
        <div className="org-chart" style={{ transform: `scale(${zoomLevel})` }}>
          {organizationData.map((node) => renderTreeNode(node))}
        </div>
      </div>
    );
  };

  const handleZoomIn = (): void => {
    setZoomLevel((prev) => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = (): void => {
    setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handleResetZoom = (): void => {
    setZoomLevel(1);
  };

  const handleExpandAll = (): void => {
    const allNodeIds = getAllNodeIds(organizationData);
    setExpandedNodes(allNodeIds);
  };

  const getAllNodeIds = (nodes: OrgNode[]): Set<string> => {
    const ids = new Set<string>();

    const traverse = (node: OrgNode) => {
      ids.add(node.id);
      node.children.forEach((child) => traverse(child));
    };

    nodes.forEach((node) => traverse(node));
    return ids;
  };

  return (
    <>
      {isLoading && <Loader />}

      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            {/* LEFT SIDE */}
            <div className="my-auto mb-2">
              <h2 className="mb-1">Organization Chart</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Organization</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Organization Chart
                  </li>
                </ol>
              </nav>
            </div>

            {/* RIGHT SIDE */}
            <div className="d-flex align-items-center gap-3">
              <div style={{ minWidth: "250px" }}>
                <CommonSelect
                  className="select"
                  options={getCompanyOptions()}
                  defaultValue={
                    selectedCompany
                      ? {
                        value: `${selectedCompany.id}-${selectedCompany.type}`,
                        label: `${selectedCompany.name} - ${selectedCompany.type}`,
                      }
                      : undefined
                  }
                  onChange={handleCompanyChange}
                  placeHolder="Select Company"
                />
              </div>

              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>


          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex align-items-end justify-content-end gap-4 flex-wrap">

                {/* Zoom Control */}
                <div className="flex-shrink-0">
                  <label className="form-label">Zoom Control</label>
                  <div className="btn-group">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= 0.5}
                    >
                      <i className="ti ti-zoom-out"></i>
                    </button>

                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleResetZoom}
                    >
                      {Math.round(zoomLevel * 100)}%
                    </button>

                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= 2}
                    >
                      <i className="ti ti-zoom-in"></i>
                    </button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex-shrink-0">
                  <label className="form-label">Actions</label>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      className="btn btn-primary"
                      onClick={() => setExpandedNodes(new Set())}
                    >
                      <i className="ti ti-collapse me-1"></i> Collapse All
                    </button>

                    <button
                      className="btn btn-success"
                      onClick={handleExpandAll}
                    >
                      <i className="ti ti-expand me-1"></i> Expand All
                    </button>

                    <button
                      className="btn btn-info"
                      onClick={handleFindCurrentUser}
                    >
                      <i className="ti ti-user-search me-1"></i> Find Me
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>



          <div className="card">
            <div className="card-header">
              <h5>
                {selectedCompany
                  ? `${selectedCompany.name} Organization Structure`
                  : "Organization Chart"}
                {/* <span className="text-muted ms-2">
                  (
                  {organizationData.reduce(
                    (acc, node) => acc + countAllNodes(node),
                    0
                  )}{" "}
                  total nodes)
                </span> */}
              </h5>
              <div className="legend">
                <div className="legend-item current-user">
                  <span className="legend-color"></span>
                  <span>Current User</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color ceo"></span>
                  <span>CEO/Leadership</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color department"></span>
                  <span>Department</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color head"></span>
                  <span>Department Head</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color employee"></span>
                  <span>Employee</span>
                </div>
              </div>
            </div>
            <div className="card-body">
              {organizationData.length === 0 ? (
                <div className="text-center py-5">
                  <i
                    className="ti ti-users"
                    style={{ fontSize: "3rem", color: "#ddd" }}
                  ></i>
                  <h5 className="mt-3">No organization data available</h5>
                  <p className="text-muted">
                    Select a company to view its organization structure
                  </p>
                </div>
              ) : (
                renderHierarchicalView()
              )}
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

const countAllNodes = (node: OrgNode): number => {
  let count = 1;
  if (node.children) {
    node.children.forEach((child) => {
      count += countAllNodes(child);
    });
  }
  return count;
};

export default OrganizationChart;